import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { CreateVaultExportFormValues } from '../create-vault-export-form';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import SharedDrivesAutocomplete from '../../../../shared/shared-drives-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  SharedDriveModel,
  VAULT_EXPORT_REGION_OPTIONS,
} from '../../../../../types';
import { useEffect, useState } from 'react';
import VaultTooltipWithLink from '../../../automation/decommissioning/decom-policy-form/form-steps/shared/vault-tooltip-with-link';

import {
  dataScopeOptions,
  searchMethodOptions,
  timeZones,
} from '../vault-export-constants';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface CreateDriveVaultExportProps {
  control: Control<CreateVaultExportFormValues>;
  errors: FieldErrors<CreateVaultExportFormValues>;
  register: UseFormRegister<CreateVaultExportFormValues>;
  selectedSearchMethod: string;
  setValue: UseFormSetValue<CreateVaultExportFormValues>;
}

export default function CreateDriveVaultExport({
  control,
  errors,
  register,
  selectedSearchMethod,
  setValue,
}: Readonly<CreateDriveVaultExportProps>) {
  const theme = useTheme();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [driveSearch, setDriveSearch] = useState('');
  const [selectedDrives, setSelectedDrives] = useState<SharedDriveModel[]>([]);

  const selectedDataScope =
    useWatch({
      control,
      name: 'dataScope',
    }) || 'ALL_DATA';

  useEffect(() => {
    setValue('searchMethodTarget', '');
    setUserSearch('');
    setDriveSearch('');
    setSelectedDrives([]);
  }, [selectedSearchMethod, setValue]);

  return (
    <>
      <Controller
        name="dataScope"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '100%', mt: 1 }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Source
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              sx={{ width: '100%' }}
            >
              <TextField
                {...field}
                select
                required
                error={!!errors.dataScope}
                sx={{ width: '50%' }}
              >
                {dataScopeOptions
                  .filter(
                    (option) =>
                      option.value === 'ALL_DATA' ||
                      option.value === 'HELD_DATA'
                  )
                  .map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
              </TextField>
              {selectedDataScope === 'HELD_DATA' && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10 }}
                  >
                    Dates are in (GMT+00:00) Coordinated Universal Time
                  </Typography>
                  <VaultTooltipWithLink
                    preset="heldData"
                    sxIcon={{ mb: 0.5 }}
                  />
                </Stack>
              )}
            </Stack>
          </FormControl>
        )}
      />

      <Controller
        name="searchMethod"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '50%', mt: 1 }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Entity
            </Typography>
            <TextField {...field} select required error={!!errors.searchMethod}>
              {searchMethodOptions
                .filter(
                  (option) =>
                    option.value === 'ACCOUNT' ||
                    option.value === 'ORG_UNIT' ||
                    option.value === 'SHARED_DRIVE'
                )
                .map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>
        )}
      />

      <Controller
        name="searchMethodTarget"
        control={control}
        render={({ field }) => {
          const selectedTargets = field.value
            ? field.value.split(',').filter(Boolean)
            : [];

          if (selectedSearchMethod === 'ORG_UNIT') {
            return (
              <FormControl sx={{ width: '50%', mt: 1 }} size="small">
                <OrganizationsAutoComplete
                  autoCompleteValue={field.value || ''}
                  setAutoCompleteValue={field.onChange}
                  size="small"
                  hasError={!!errors.searchMethodTarget}
                  helperText={errors?.searchMethodTarget?.message}
                  disableParents={true}
                />
              </FormControl>
            );
          }

          const addTarget = (target: string) => {
            if (!target || selectedTargets.includes(target)) return;
            field.onChange([...selectedTargets, target].join(','));
          };

          const removeTarget = (target: string) =>
            field.onChange(
              selectedTargets.filter((item) => item !== target).join(',')
            );

          return (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ width: '50%', mt: 1 }}>
                {selectedSearchMethod === 'ACCOUNT' ? (
                  <SharedAutocomplete
                    sources={['users']}
                    autocompleteValue={userSearch}
                    setAutoCompleteValue={(value) => {
                      addTarget(value);
                      setUserSearch('');
                    }}
                    label="Account Email Addresses"
                    required
                    errors={!!errors.searchMethodTarget}
                    helperText={errors?.searchMethodTarget?.message}
                  />
                ) : (
                  <SharedDrivesAutocomplete
                    autocompleteValue={driveSearch}
                    setAutoCompleteValue={setDriveSearch}
                    onDriveSelect={(drive) => {
                      if (drive) {
                        addTarget(drive.id);
                        setSelectedDrives((current) =>
                          current.some((item) => item.id === drive.id)
                            ? current
                            : [...current, drive]
                        );
                      }
                      setDriveSearch('');
                    }}
                    size="small"
                    required
                    errors={!!errors.searchMethodTarget}
                    helperText={errors?.searchMethodTarget?.message}
                  />
                )}
              </Box>
              {selectedTargets.length > 0 && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {selectedTargets.map((target) => {
                    const drive = selectedDrives.find(
                      (item) => item.id === target
                    );
                    return (
                      <AvatarChip
                        key={target}
                        type={drive ? 'Shared Drive' : 'Unknown'}
                        email={target}
                        label={drive?.name || target}
                        onDelete={() => removeTarget(target)}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        }}
      />

      {(selectedSearchMethod === 'ACCOUNT' ||
        selectedSearchMethod === 'ORG_UNIT') && (
        <Controller
          name="sharedDrivesOption"
          control={control}
          defaultValue="INCLUDED"
          render={({ field }) => (
            <FormControl>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ mt: 1, mb: 0.5 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Shared Drives
                </Typography>
                <VaultTooltipWithLink
                  preset="sharedDrives"
                  sxIcon={{ mb: 0.5 }}
                />
              </Stack>
              <RadioGroup {...field}>
                <FormControlLabel
                  value="INCLUDED"
                  control={<Radio size="small" />}
                  label="Include Shared Drives"
                />
                <FormControlLabel
                  value="NOT_INCLUDED"
                  control={<Radio size="small" />}
                  label="Do Not Include Shared Drives"
                />
                <FormControlLabel
                  value="INCLUDED_IF_ACCOUNT_IS_NOT_A_MEMBER"
                  control={<Radio size="small" />}
                  label="Include only if documents shared directly"
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      )}

      {selectedDataScope !== 'HELD_DATA' && (
        <Controller
          name="timeZone"
          control={control}
          render={({ field }) => (
            <FormControl sx={{ width: '50%', mt: 1 }} size="small">
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Timezone
                </Typography>
                <VaultTooltipWithLink
                  preset="vaultTimezone"
                  sxIcon={{ mb: 0.5 }}
                />
              </Stack>
              <TextField {...field} select required error={!!errors.timeZone}>
                {timeZones.map((timeZone) => (
                  <MenuItem key={timeZone} value={timeZone}>
                    {timeZone}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          )}
        />
      )}

      <Stack direction="row" gap={2} sx={{ width: '75%' }}>
        <Controller
          name="startTime"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Date Modified (optional)
                </Typography>
                <VaultTooltipWithLink
                  preset="dateModified"
                  sxIcon={{ mb: 0.5 }}
                />
              </Stack>
              <DatePicker
                label="Start Date"
                value={field.value ? dayjs(field.value) : null}
                onChange={(value) =>
                  field.onChange(value?.format('YYYY-MM-DD') || '')
                }
                slotProps={{
                  textField: { size: 'small', error: !!errors.startTime },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Format: mm/dd/yyyy
              </Typography>
            </FormControl>
          )}
        />
        <Controller
          name="endTime"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                &nbsp;
              </Typography>
              <DatePicker
                label="End Date"
                value={field.value ? dayjs(field.value) : null}
                onChange={(value) =>
                  field.onChange(value?.format('YYYY-MM-DD') || '')
                }
                slotProps={{
                  textField: { size: 'small', error: !!errors.endTime },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Format: mm/dd/yyyy
              </Typography>
            </FormControl>
          )}
        />
        <Controller
          name="methodTargetVersionDate"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Version Date (optional)
                </Typography>
                <VaultTooltipWithLink
                  preset="driveVersionDate"
                  sxIcon={{ mb: 0.5 }}
                />
              </Stack>
              <DatePicker
                label="Version Date"
                value={field.value ? dayjs(field.value) : null}
                onChange={(value) =>
                  field.onChange(value?.format('YYYY-MM-DD') || '')
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !!errors.methodTargetVersionDate,
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Format: mm/dd/yyyy
              </Typography>
            </FormControl>
          )}
        />
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink preset="termsOptional" sxIcon={{ mb: 0.5 }} />
        </Stack>
        <TextField
          {...register('terms')}
          placeholder="Query terms"
          size="small"
          variant="standard"
          fullWidth
          error={!!errors.terms}
          helperText={
            errors?.terms?.message ||
            'Example: from:username1 subject: Hello has:attachment'
          }
        />
      </Box>

      <Accordion
        elevation={0}
        disableGutters
        expanded={isAdvancedExpanded}
        onChange={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
        sx={{
          '&:before': { display: 'none' },
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          width: '100%',
          mt: 1,
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ color: theme.palette.colors?.primary?.[600] }} />
          }
          sx={{
            px: 0,
            minHeight: 'auto',
            width: 'fit-content',
            backgroundColor: 'transparent !important',
            '& .MuiAccordionSummary-content': { my: 0.5, flexGrow: 0, mr: 1 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.colors?.primary?.[600],
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {isAdvancedExpanded
              ? 'Hide Export Settings'
              : 'Show Export Settings'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Stack gap={2} sx={{ width: '100%', mt: 1 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="subtitle2">
                Report access levels for accounts with indirect access to Drive
                item:
              </Typography>
              <Controller
                name="showIndirectAccess"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <Switch
                    {...rest}
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
              <VaultTooltipWithLink preset="indirectAccess" />
            </Stack>

            <Controller
              name="matterRegion"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: '50%' }} size="small">
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Data Region
                    </Typography>
                    <VaultTooltipWithLink
                      preset="dataRegion"
                      sxIcon={{ mb: 0.5 }}
                    />
                  </Stack>
                  <TextField {...field} select>
                    {VAULT_EXPORT_REGION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
